import { useCallback, useContext } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { AppContext } from '@/app.context';
import { AUTH_STORAGE_KEYS } from '@shared/auth';
import { clearPersistStorage } from '@shared/query-client';
import { observabilityDb } from '@shared/observability';
import { Logger } from '@shared/lib/logger';

const logger = new Logger('clear-app-data');

/**
 * "Clear cache": everything this app keeps on the device except the
 * reader's sign-in -- the KV store, localStorage (Settings included), the
 * query cache and its IndexedDB copy, downloaded issues, the debug log and
 * the runtime caches -- then a reload. The precache stays, so the app still
 * opens offline. Confirms first; resolves `false` if the reader cancels.
 *
 * Used by Settings (Offline & storage) and the About dialog.
 */
export function useClearAppData(): () => Promise<boolean> {
	const queryClient = useQueryClient();
	const { kv, db } = useContext(AppContext);

	return useCallback(async () => {
		if (
			!confirm(
				'Clear the cache? Downloaded issues and your settings are removed too, and the app reloads.',
			)
		) {
			return false;
		}

		kv.clear();

		// Keep the auth keys so clearing the cache doesn't sign the reader out.
		const authKeys = new Set<string>(AUTH_STORAGE_KEYS);
		for (const key of Object.keys(localStorage)) {
			if (!authKeys.has(key)) {
				localStorage.removeItem(key);
			}
		}

		queryClient.clear();
		await clearPersistStorage().catch((error: unknown) => {
			logger.error('Failed to clear the persisted query cache', error);
		});

		if (db) {
			await db.clear().catch((error: unknown) => {
				logger.error('Failed to clear the offline downloads DB', error);
			});
		}

		await Promise.all([
			observabilityDb.events.clear(),
			observabilityDb.documents.clear(),
			observabilityDb.meta.clear(),
		]).catch((error: unknown) => {
			logger.error('Failed to clear the observability DB', error);
		});

		// Runtime caches only: the precache is the app shell itself.
		if ('caches' in window) {
			const names = await caches.keys();
			await Promise.all(
				names
					.filter((name) => !name.startsWith('workbox-precache'))
					.map((name) => caches.delete(name)),
			);
		}

		window.location.reload();
		return true;
	}, [queryClient, kv, db]);
}
